import type { TFunction } from "i18next";
import { Copy } from "lucide-react";
import type { JSX } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { chunkGaps } from "@/lib/noteLineGaps";
import {
  type NoteSection,
  type SoapSectionKind,
  splitNoteSections,
} from "@/lib/noteSections";
import { countVerifyMarkers } from "@/lib/verifyMarkers";
import { useVerifyGate } from "../workspace/VerifyReview";
import { copySectionToClipboard } from "./copyNote";

const SECTION_NAME_KEYS = {
  subjective: "visit.workspace.sectionNames.subjective",
  objective: "visit.workspace.sectionNames.objective",
  assessment: "visit.workspace.sectionNames.assessment",
  assessmentAndPlan: "visit.workspace.sectionNames.assessmentAndPlan",
  plan: "visit.workspace.sectionNames.plan",
} as const satisfies Record<SoapSectionKind, string>;

// The section's name for its button and toast: the heading as written, or the
// full name for "S:", "A/P" and other short forms.
export const sectionName = (section: NoteSection, t: TFunction): string =>
  section.kind && section.heading.length <= 3
    ? t(SECTION_NAME_KEYS[section.kind])
    : section.heading;

// The text before the first heading gets its own copy button when it is
// more than a title: a letter's opening ("Dear Dr…", "Thank you for seeing…")
// is pasted like any section. A one-line title ("**SOAP note**") is not.
export const preambleIsCopyable = (preamble: string): boolean => {
  const lines = preamble
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 1 || (lines[0]?.length ?? 0) > 60;
};

// The opening's markers: the first ones of the note.
const preambleMarkerScope = (
  text: string,
): { first: number; count: number } => ({
  first: 0,
  count: countVerifyMarkers(splitNoteSections(text).preamble),
});

// The n-th section's markers, as [first, first + count) of the whole note's.
export const sectionMarkerScope = (
  text: string,
  index: number,
): { first: number; count: number } => {
  const { preamble, sections } = splitNoteSections(text);
  let first = countVerifyMarkers(preamble);
  for (const section of sections.slice(0, index))
    first += countVerifyMarkers(section.full);
  return { first, count: countVerifyMarkers(sections[index]?.full ?? "") };
};

// The copy button beside a section heading (or the opening). It floats right
// so only the first line wraps around it. Unresolved "[verify]" values in the
// section are reviewed first; the text is then taken from the reviewed note.
const SectionCopyButton = ({
  body,
  name,
  scope,
  review,
}: {
  body: string;
  name: string;
  scope: (text: string) => { first: number; count: number };
  review: (text: string) => string | undefined;
}): JSX.Element => {
  const { t } = useTranslation();
  const gate = useVerifyGate();
  const label = t("visit.workspace.copySection", { name });
  return (
    <Button
      aria-label={label}
      className="float-right -mr-2 -mt-2 ml-2 size-11 text-muted-foreground hover:text-foreground lg:-mt-1 lg:size-8 [&_svg]:size-4"
      data-testid="copy-section"
      disabled={!body.trim()}
      size="icon"
      title={label}
      type="button"
      variant="ghost"
      onClick={() => {
        if (!gate) {
          void copySectionToClipboard(body, name);
          return;
        }
        gate.request({
          reason: "copy",
          label: t("visit.workspace.verify.review.copySection", { name }),
          scope,
          proceed: (text) => {
            void copySectionToClipboard(review(text) ?? body, name);
          },
        });
      }}
    >
      <Copy aria-hidden />
    </Button>
  );
};

// A section at the note's reading size and line height, so its top margin
// (in lines) matches a blank line of the note.
const SECTION_CLASS =
  "flow-root text-base leading-[1.65] sm:text-[17px] [&_.editor-markdown>:first-child]:mt-0";

interface Props {
  result: string;
  // Open the editor at the n-th "[verify]" marker. Without it the markers are
  // still shown as chips, but as labels.
  onVerifyClick?: (index: number) => void;
  // Review card (VITE_REVIEW_CARD_ENABLED): highlight each flagged value
  // instead of a chip after it.
  highlightFlags?: boolean;
}

// A read-only note with a copy button beside each section heading, for EMRs
// with a separate field per section (S, O, A, P). The button copies the
// section as plain text without its heading line. A note with fewer than two
// headings renders as before, without buttons.
export const SectionedNote = ({
  result,
  onVerifyClick,
  highlightFlags = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { preamble, sections } = useMemo(
    () => splitNoteSections(result),
    [result],
  );
  // Blank lines before each section, as written (the copied text keeps
  // them): the preamble first, then each section.
  const gaps = useMemo(
    () =>
      chunkGaps(result, [
        ...(preamble ? [preamble] : []),
        ...sections.map((section) => section.full),
      ]),
    [result, preamble, sections],
  );
  const gapAt = (index: number): number =>
    gaps[(preamble ? 1 : 0) + index] ?? 0;
  // A section starts as many lines lower as the note has blank lines before
  // it, at the note's reading size (NOTE_READING_CLASS).
  const gapProps = (gap: number) => ({
    "data-gap": gap || undefined,
    style: gap ? { marginTop: `${gap}lh` } : undefined,
  });
  const chips = (offset: number) => ({
    lineGaps: true,
    verifyChips: {
      label: t("visit.workspace.verify.chip"),
      title: t("visit.workspace.verify.tooltip"),
      interactive: !!onVerifyClick,
      offset,
    },
    verifyFlags: highlightFlags
      ? {
          label: t("visit.workspace.verify.card.highlight"),
          title: t("visit.workspace.verify.tooltip"),
          emptyLabel: t("visit.workspace.verify.chip"),
        }
      : undefined,
    onVerifyClick,
  });

  if (sections.length < 2)
    return <FileResultRendererMD result={result} {...chips(0)} />;

  const openingName = t("visit.workspace.sectionNames.opening");
  // Each section's first marker index in the whole note.
  let offset = countVerifyMarkers(preamble);
  return (
    <div data-testid="sectioned-note">
      {preamble &&
        (preambleIsCopyable(preamble) ? (
          <section
            aria-label={openingName}
            className={SECTION_CLASS}
            data-testid="note-section"
          >
            <SectionCopyButton
              body={preamble}
              name={openingName}
              review={(text) => splitNoteSections(text).preamble}
              scope={preambleMarkerScope}
            />
            <FileResultRendererMD result={preamble} {...chips(0)} />
          </section>
        ) : (
          <FileResultRendererMD result={preamble} {...chips(0)} />
        ))}
      {sections.map((section, index) => {
        const name = sectionName(section, t);
        const sectionOffset = offset;
        offset += countVerifyMarkers(section.full);
        return (
          // The button floats right so only the heading line wraps around it
          // and the section's text keeps the full width below.
          <section
            // biome-ignore lint/suspicious/noArrayIndexKey: sections have no id and keep their order.
            key={index}
            aria-label={name}
            className={SECTION_CLASS}
            data-testid="note-section"
            {...gapProps(gapAt(index))}
          >
            <SectionCopyButton
              body={section.body}
              name={name}
              review={(text) => splitNoteSections(text).sections[index]?.body}
              scope={(text) => sectionMarkerScope(text, index)}
            />
            <FileResultRendererMD
              result={section.full}
              {...chips(sectionOffset)}
            />
          </section>
        );
      })}
    </div>
  );
};
